import React, { useEffect } from 'react';
import { StyleSheet, View } from 'react-native';
import { useScrollspy } from '@sg/garnish';
import { type CustomizationMachineTypes } from '@sg/shared-fe/features/customization';

import { CustomizationIngredientsGrid } from '@order/features/customization';

import { useStepperTimeout } from '../../../../../ProductDetailsContentV2/components/ProductDetailsIngredientsView/hooks';
import { CustomizationModificationsGroupCard } from '../../../../components';
import { useModificationGroupHelperText } from '../../../../hooks';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationIngredientsGridPanel = (props: Props) => {
  const {
    modifierGroups,
    modificationLimits,
    activeTargetId,
    activeModificationsMap,
    maxModificationsCount,
    addIngredientModification,
    removeIngredientModification,
    getFormattedQuantity,
  } = props;

  const getSectionHelperText = useModificationGroupHelperText();
  const { activeStepperId, setActiveStepperId, resetStepperTimeout } = useStepperTimeout(); // prettier-ignore

  const { helpers, setActiveTargetId } = useScrollspy({
    defaultActiveTargetId: activeTargetId ?? undefined,
  });

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!activeTargetId) return;

    setActiveTargetId(activeTargetId);
  }, [activeTargetId, setActiveTargetId]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={styles.ingredientsGridContainer}>
      <CustomizationIngredientsGrid.Container
        register={helpers.scrollView.register}
        deregister={helpers.scrollView.deregister}
        storeContentSize={helpers.scrollView.storeContentSize}
        storeSize={helpers.scrollView.storeSize}
        trackScroll={helpers.scrollView.trackScroll}
      >
        {modifierGroups?.map((group) => {
          const freeAggregateQuantity =
            modificationLimits[group.name]?.freeAggregateQuantity;

          return (
            <CustomizationIngredientsGrid.Section
              key={group.id}
              sectionId={group.id}
              ingredientKindName={group.displayName}
              register={helpers.target.register}
              deregister={helpers.target.deregister}
              formattedQuantity={getFormattedQuantity(group.name)}
              helperText={getSectionHelperText({
                freeAggregateQuantity,
                ingredientKind: group.modificationIngredientKind,
              })}
            >
              <CustomizationIngredientsGrid.SectionStack>
                {group.modifications.map((modification) => (
                  <CustomizationModificationsGroupCard
                    key={modification.id}
                    activeModificationsMap={activeModificationsMap}
                    activeStepperId={activeStepperId}
                    groupLimits={modificationLimits?.[group.name]}
                    maxModificationsCount={maxModificationsCount}
                    modification={modification}
                    onIncrement={addIngredientModification}
                    onDecrement={removeIngredientModification}
                    resetStepperTimeout={resetStepperTimeout}
                    setActiveStepperId={setActiveStepperId}
                  />
                ))}
              </CustomizationIngredientsGrid.SectionStack>
            </CustomizationIngredientsGrid.Section>
          );
        })}
      </CustomizationIngredientsGrid.Container>
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  ingredientsGridContainer: {
    flex: 1,
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  activeModificationsMap: Map<string, CustomizationMachineTypes.Modification>;
  modifierGroups: CustomizationMachineTypes.ExtendedModifierGroup[] | undefined;
  modificationLimits: CustomizationMachineTypes.ModificationGroupLimits;
  maxModificationsCount: number | undefined;
  activeTargetId: string | null | undefined;
  addIngredientModification: (ingredientId: string) => void;
  removeIngredientModification: (ingredientId: string) => void;
  getFormattedQuantity: (groupName: string) => string | undefined;
};
